/**
 * core · sessão (spec 03 §2 · tela 1.1).
 *
 * Estes são os **contratos nominais** da spec, não as rotas default da biblioteca
 * de auth: a tela fala com `/auth/login`, `/auth/redefinir-senha`,
 * `/auth/aceitar-convite` — nomes do produto —, e a API é que traduz para o
 * protocolo do Better Auth. A tradução fica num lugar só; trocar de biblioteca não
 * reescreve nenhuma tela.
 */
import { z } from 'zod'
import { email, senha } from './comum.js'

/**
 * Quantos dias vive a sessão de um dispositivo em que a pessoa confia.
 *
 * Mora aqui, e não no servidor, porque o número aparece nos DOIS lados: a tela 1.1
 * escreve "Confiar neste dispositivo por 30 dias" e a política do Better Auth expira
 * a sessão em 30 dias. Duas constantes seriam duas verdades, e a que a pessoa lê é a
 * que ninguém checa — o mesmo motivo do `MINIMO_DA_SENHA` (DS-96).
 */
export const DIAS_DE_DISPOSITIVO_CONFIAVEL = 30

export const entradaDeLogin = z.object({
  email,
  // No login a senha é apenas OBRIGATÓRIA. Cobrar tamanho aqui contaria ao atacante
  // quais tentativas nem vale a pena fazer (spec 04-TELAS/1.1 §Migração).
  senha: z.string().min(1, 'Informe a senha.'),
  /**
   * "Confiar neste dispositivo por 30 dias" — a caixa da tela 1.1.
   *
   * Ausente é o mesmo que `false`: sessão de NAVEGADOR, que morre quando a janela
   * fecha. Confiar é o ato explícito, nunca o default — um cookie de 30 dias num
   * computador compartilhado é a conta inteira entregue a quem sentar depois.
   */
  manterConectado: z.boolean().optional(),
})
export type EntradaDeLogin = z.infer<typeof entradaDeLogin>

/**
 * O que o login responde.
 *
 * `proximoPasso` é o que a tela 1.1 lê para decidir qual view mostrar — e o motivo
 * de a resposta não ser um simples "ok": quem tem segundo fator obrigatório e ainda
 * não o constituiu **não recebe sessão** e precisa ser levado ao enrolamento, não a
 * uma tela de erro (spec 05 §9).
 */
export const proximoPasso = z.enum(['entrou', 'verificacao_2fa', 'enrolamento_2fa'])
export type ProximoPasso = z.infer<typeof proximoPasso>

export const respostaDeLogin = z.object({ proximoPasso })
export type RespostaDeLogin = z.infer<typeof respostaDeLogin>

/** Identidade do token consumido, necessária para seguir ao 2FA sem sessão parcial. */
export const respostaDeDefinicaoDeSenha = respostaDeLogin.extend({ email })
export type RespostaDeDefinicaoDeSenha = z.infer<typeof respostaDeDefinicaoDeSenha>

export const entradaDeEsqueciSenha = z.object({ email })

/**
 * Resposta de "esqueci a senha": SEMPRE a mesma.
 *
 * Não-enumeração é o ponto inteiro desta rota (spec 04-TELAS/1.1): existir ou não a
 * conta, a tela mostra a view SENT. Por isso o contrato não tem campo nenhum que
 * possa variar — nem `enviado: boolean`, que já seria resposta diferente.
 */
export const respostaNeutra = z.object({ ok: z.literal(true) })

export const entradaDeRedefinirSenha = z.object({ token: z.string().min(1), senha })
export const entradaDeAceitarConvite = z.object({ token: z.string().min(1), senha })

/** Código do aplicativo autenticador: 6 dígitos. */
export const codigo2fa = z.string().regex(/^\d{6}$/, 'O código tem 6 dígitos.')

/** Código de recuperação: dois blocos de 5, entregues no enrolamento. */
export const codigoDeRecuperacao = z.string().regex(/^[A-Za-z0-9]{5}-[A-Za-z0-9]{5}$/)

/**
 * Na VERIFICAÇÃO do login, os dois valem.
 *
 * A tela 1.1 diz, embaixo do campo, "sem acesso ao aplicativo? use um código de
 * recuperação" — e uma regra que só aceita 6 dígitos transformaria essa frase em
 * mentira: o código de recuperação seria recusado pela validação antes de o
 * servidor sequer olhar para ele. É a diferença entre ter saída e parecer ter.
 */
export const codigoDeVerificacao = z.union([codigo2fa, codigoDeRecuperacao], {
  error: 'Informe o código de 6 dígitos ou um código de recuperação.',
})

/** No ENROLAMENTO só o aplicativo confirma: o código de recuperação ainda não vale. */
export const entradaDe2fa = z.object({ codigo: codigo2fa })
export const entradaDeVerificacao2fa = z.object({ codigo: codigoDeVerificacao })

/**
 * As DUAS etapas do login que ainda abrem sessão — e por isso carregam a confiança.
 *
 * Elas viviam montadas dentro do controlador da API, e a consequência não era
 * estética: `manterConectado` atravessava a validação por acidente de estrutura, sem
 * nenhum lado declarar que devia atravessar. A tela mandava a preferência no primeiro
 * `POST /auth/login`, o login parava no segundo fator, e a sessão nascia sem ela — a
 * pessoa marcava "confiar neste dispositivo" e era deslogada ao fechar o navegador.
 *
 * Contrato é o lugar onde isso se declara uma vez e os dois lados leem. Quem abre
 * sessão pergunta pela confiança; quem não abre (o `2fa/enroll`) não pergunta.
 */
export const entradaDeVerificacaoDeLogin = entradaDeLogin.extend(entradaDeVerificacao2fa.shape)
export type EntradaDeVerificacaoDeLogin = z.infer<typeof entradaDeVerificacaoDeLogin>

export const entradaDeConfirmacaoDeEnrolamento = entradaDeLogin.extend(entradaDe2fa.shape)
export type EntradaDeConfirmacaoDeEnrolamento = z.infer<typeof entradaDeConfirmacaoDeEnrolamento>

/** O enrolamento NÃO abre sessão — pede só a credencial, e nem sempre a senha. */
export const entradaDeEnrolamento = entradaDeLogin.pick({ email: true, senha: true }).partial({ senha: true })
export type EntradaDeEnrolamento = z.infer<typeof entradaDeEnrolamento>

/**
 * O enrolamento devolve o segredo e os códigos de recuperação UMA vez.
 *
 * "Uma vez" é contrato, não implementação: depois de confirmado, nenhuma rota
 * devolve os códigos de novo — quem os perdeu passa pelo reset do administrador
 * (`POST /usuarios/:id/resetar-2fa`), que é o caminho de aparelho perdido.
 */
export const respostaDeEnrolamento = z.object({
  segredo: z.string(),
  uri: z.string(),
  codigosDeRecuperacao: z.array(z.string()),
})
export type RespostaDeEnrolamento = z.infer<typeof respostaDeEnrolamento>

export const sessaoAtual = z.object({
  usuarioId: z.string(),
  email: z.string(),
  nome: z.string(),
  nivel: z.string(),
})
export type SessaoAtual = z.infer<typeof sessaoAtual>
